import React, { useEffect, useState } from "react";
import { Form, Modal } from "react-bootstrap";
import { buildQrSvg, normalizeVehicleNumber, printQrSticker } from "./qrStickerPrint";

/**
 * "Generate QR Code": type a vehicle number, preview its QR and print it as a full A2 sticker
 * for the truck's windshield. `initialVehicle` pre-fills the number; `suggestions` (optional
 * list of vehicle numbers) feeds the input's autocomplete.
 */
export function QrCodeGeneratorDialog({ show, handleClose, initialVehicle = "", suggestions = [] }) {
  const [vehicle, setVehicle] = useState("");
  const [svg, setSvg] = useState("");
  const [printing, setPrinting] = useState(false);
  const number = normalizeVehicleNumber(vehicle);

  useEffect(() => {
    if (show) setVehicle(normalizeVehicleNumber(initialVehicle));
  }, [show, initialVehicle]);

  useEffect(() => {
    let alive = true;
    if (!number) {
      setSvg("");
      return undefined;
    }
    buildQrSvg(number).then((markup) => alive && setSvg(markup)).catch(() => alive && setSvg(""));
    return () => {
      alive = false;
    };
  }, [number]);

  const print = async () => {
    setPrinting(true);
    try {
      await printQrSticker(number);
    } finally {
      setPrinting(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Generate QR Code</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-5">
          <Form.Label className="required">Vehicle Number</Form.Label>
          <Form.Control
            autoFocus
            list="qr-generator-vehicles"
            placeholder="e.g. KA09IS7654"
            value={vehicle}
            onChange={(e) => setVehicle(normalizeVehicleNumber(e.target.value))}
            onKeyDown={(e) => e.key === "Enter" && number && print()}
          />
          <datalist id="qr-generator-vehicles">
            {suggestions.map((s) => <option key={s} value={s} />)}
          </datalist>
          <Form.Text className="text-muted">Spaces are not allowed; letters are capitalised automatically.</Form.Text>
        </Form.Group>

        <div className="d-flex flex-column align-items-center border rounded p-4 bg-white">
          {svg ? (
            <>
              <div style={{ width: 220, height: 220 }} dangerouslySetInnerHTML={{ __html: svg }} />
              <div className="text-muted fs-8 mt-2">Powered by Revcon Technologies • revcontech.com</div>
            </>
          ) : (
            <div className="text-muted py-10">Enter a vehicle number to preview its QR code</div>
          )}
        </div>

        <div className="text-muted fs-7 mt-4">
          Prints on one <b>A2</b> page (420 × 594 mm) with the QR as large as the page allows. In the print
          dialog choose paper size <b>A2</b> and scale <b>100%</b>, or <b>Save as PDF</b> to send it to a print shop.
        </div>
      </Modal.Body>
      <Modal.Footer>
        <button type="button" className="btn btn-light" onClick={handleClose}>Close</button>
        <button type="button" className="btn btn-primary" disabled={!number || printing} onClick={print}>
          <i className="fa fa-print me-2" />
          Print A2
        </button>
      </Modal.Footer>
    </Modal>
  );
}
